<script setup lang="ts">
import type { Placement, TriggerEvent } from "floating-vue";
import type { LazyPopperComponent, PopperTriggers } from "../Popper/lazyPopper";
import { PopperContent, SHOW_EVENT_MAP } from "floating-vue";
import { computed, nextTick, onBeforeUnmount, provide, ref, useAttrs, useId, watch } from "vue";
import {
  activationEventShowsPopper,
  lazyPopperComponent,
  popperThemeClasses,
  resolveShowEvents,
  useLazyPopperActivation,
} from "../Popper/lazyPopper";
import {
  closestMenuItem,
  DROPDOWN_MENU_KEY,
  FOCUSABLE_SELECTOR,
  focusMenuItemForKey,
  initialMenuItem,
  isMenuContent,
} from "./dropdownMenu";

interface Props {
  theme?: string
  referenceNode?: (() => Element) | null
  shown?: boolean
  showGroup?: string
  ariaId?: string | null
  disabled?: boolean
  positioningDisabled?: boolean
  placement?: Placement
  delay?: string | number | { show: number, hide: number }
  distance?: number | string
  skidding?: number | string
  triggers?: TriggerEvent[]
  showTriggers?: PopperTriggers
  hideTriggers?: PopperTriggers
  popperTriggers?: TriggerEvent[]
  popperShowTriggers?: PopperTriggers
  popperHideTriggers?: PopperTriggers
  container?: string | Element | boolean
  boundary?: string | Element
  strategy?: "absolute" | "fixed"
  autoHide?: boolean | ((event: Event) => boolean)
  handleResize?: boolean
  instantMove?: boolean
  eagerMount?: boolean
  popperClass?: string | string[] | Record<string, boolean>
  computeTransformOrigin?: boolean
  autoMinSize?: boolean
  autoSize?: boolean | "min" | "max"
  autoMaxSize?: boolean
  autoBoundaryMaxSize?: boolean
  preventOverflow?: boolean
  overflowPadding?: number | string
  arrowPadding?: number | string
  arrowOverflow?: boolean
  flip?: boolean
  shift?: boolean
  shiftCrossAxis?: boolean
  noAutoFocus?: boolean
  disposeTimeout?: number
}

interface ShowOptions {
  event?: Event
  skipDelay?: boolean
  force?: boolean
}

/**
 * Drop-in replacement for floating-vue's `Dropdown` (same props, events, slots, exposed methods and DOM)
 * that only creates the popper on the first interaction with the reference, when `shown` becomes true or
 * when `show()` is called. Overviews render hundreds of dropdowns that are never opened; mounting a
 * floating-vue Popper for each of them was the single largest cost of rendering a row.
 */
defineOptions({
  name: "VDropdown",
  inheritAttrs: false,
});

// Optional props default to `undefined` on purpose: an absent prop must leave floating-vue's theme default
// (for example `autoHide: true` for dropdowns) in place instead of forcing a value.
const props = withDefaults(defineProps<Props>(), {
  theme: "dropdown",
  referenceNode: null,
  shown: false,
  showGroup: undefined,
  ariaId: null,
  disabled: undefined,
  positioningDisabled: undefined,
  placement: undefined,
  delay: undefined,
  distance: undefined,
  skidding: undefined,
  triggers: undefined,
  showTriggers: undefined,
  hideTriggers: undefined,
  popperTriggers: undefined,
  popperShowTriggers: undefined,
  popperHideTriggers: undefined,
  container: undefined,
  boundary: undefined,
  strategy: undefined,
  autoHide: undefined,
  handleResize: undefined,
  instantMove: undefined,
  eagerMount: undefined,
  popperClass: undefined,
  computeTransformOrigin: undefined,
  autoMinSize: undefined,
  autoSize: undefined,
  autoMaxSize: undefined,
  autoBoundaryMaxSize: undefined,
  preventOverflow: undefined,
  overflowPadding: undefined,
  arrowPadding: undefined,
  arrowOverflow: undefined,
  flip: undefined,
  shift: undefined,
  shiftCrossAxis: undefined,
  noAutoFocus: undefined,
  disposeTimeout: undefined,
});

// Vue resolves `emit("applyShow")` for `@apply-show` listeners too, so consumers of floating-vue's
// kebab-case event names keep working.
const emit = defineEmits<{
  "show": []
  "hide": []
  "update:shown": [shown: boolean]
  "applyShow": []
  "applyHide": []
  "closeGroup": []
  "closeDirective": []
  "autoHide": []
  "resize": []
}>();

const attrs = useAttrs();
const LazyPopper = lazyPopperComponent();

const rootEl = ref<HTMLElement | null>(null);
const popper = ref<InstanceType<LazyPopperComponent> | null>(null);
const popperContent = ref<InstanceType<typeof PopperContent> | null>(null);
const active = ref(props.shown || props.eagerMount === true);
const isShown = ref(false);

const themeClasses = computed(() => popperThemeClasses(props.theme));

watch(() => props.shown, (shown) => {
  if (shown) {
    active.value = true;
  }
});

function getReferenceNode(): Element | null {
  return props.referenceNode?.() ?? rootEl.value;
}

function getPopperNode(): HTMLElement | undefined {
  return popperContent.value?.$el;
}

function getTargetNodes(): Element[] {
  const root = rootEl.value;
  if (!root) {
    return [];
  }
  const popperNode = getPopperNode();
  return Array.from(root.children).filter(node => node !== popperNode);
}

async function mountPopper(): Promise<InstanceType<LazyPopperComponent> | null> {
  if (!active.value) {
    active.value = true;
    await nextTick();
  }
  return popper.value;
}

async function activate(event: Event): Promise<void> {
  if (active.value || props.disabled) {
    return;
  }
  const instance = await mountPopper();
  if (!instance) {
    return;
  }
  if (!activationEventShowsPopper(event, getTargetNodes(), showEventsOf(instance))) {
    return;
  }
  (event as Event & { usedByTooltip?: boolean }).usedByTooltip = true;
  instance.show({ event });
}

async function show(options?: ShowOptions): Promise<void> {
  const instance = await mountPopper();
  instance?.show(options);
}

function hide(options?: ShowOptions): void {
  popper.value?.hide(options);
}

function dispose(): void {
  popper.value?.dispose();
}

function onResize(): void {
  popper.value?.onResize();
}

function showEventsOf(instance: InstanceType<LazyPopperComponent>): Set<string> {
  return resolveShowEvents(instance.triggers as TriggerEvent[], instance.showTriggers as PopperTriggers);
}

// The first focusable element of the reference: the button that opens the dropdown.
function getTriggerElement(): HTMLElement | undefined {
  return getTargetNodes()
    .map(node => (node.matches(FOCUSABLE_SELECTOR) ? node : node.querySelector(FOCUSABLE_SELECTOR)))
    .find((node): node is HTMLElement => node instanceof HTMLElement);
}

// floating-vue focuses the popper when it opens (unless noAutoFocus) and leaves focus there when it closes,
// which the browser flags once the popper turns aria-hidden. Focus goes back to the trigger, unless focusing
// the trigger would reopen the popper, in which case it is dropped.
function returnFocusFromPopper(): void {
  const popperNode = getPopperNode();
  const activeElement = document.activeElement;
  if (!popperNode || !(activeElement instanceof HTMLElement) || !popperNode.contains(activeElement)) {
    return;
  }
  const instance = popper.value;
  const trigger = getTriggerElement();
  if (trigger && instance && !showEventsOf(instance).has(SHOW_EVENT_MAP.focus)) {
    trigger.focus({ preventScroll: true });
    return;
  }
  activeElement.blur();
}

// --- Menu ---
// A dropdown that holds only menu items (VDropdownItem, or custom items with `data-menu-item`) is a menu:
// the popper is a `menu` named by its trigger, focus moves to an item when it opens, the arrow keys, Home and End
// move between the items, Enter and Space activate one, Escape closes it and returns focus to the trigger, and
// Tab closes it and moves on from the trigger. With other controls in it, the items stay buttons in the tab order.

const isMenu = ref(false);
const menuTriggerId = ref<string | undefined>(undefined);
const fallbackTriggerId = `${useId()}-trigger`;
let focusOnMenuOpen = false;
let menuCheckScheduled = false;
// floating-vue emits `hide` at once and applies it on a timer, so the popper still counts as shown in between.
// Focus must not move into a popper that is on its way out.
let hideRequested = false;
// Popper content often renders after the popper opens (a `v-if="shown"` popper slot, items that load), so the
// dropdown checks again whenever the content changes while it is open.
let contentObserver: MutationObserver | null = null;

provide(DROPDOWN_MENU_KEY, { isMenu });

function scheduleMenuCheck(): void {
  if (!isShown.value || menuCheckScheduled) {
    return;
  }
  menuCheckScheduled = true;
  nextTick(() => {
    menuCheckScheduled = false;
    updateMenu();
  });
}

function observeContent(): void {
  const popperNode = getPopperNode();
  if (!popperNode || typeof MutationObserver === "undefined") {
    return;
  }
  contentObserver ??= new MutationObserver(() => scheduleMenuCheck());
  contentObserver.observe(popperNode, { childList: true, subtree: true });
}

function stopObservingContent(): void {
  contentObserver?.disconnect();
}

onBeforeUnmount(stopObservingContent);

function updateMenu(): void {
  const popperNode = getPopperNode();
  if (!isShown.value || !popperNode) {
    return;
  }
  isMenu.value = isMenuContent(popperNode);
  const trigger = getTriggerElement();
  if (!isMenu.value || !trigger) {
    return;
  }
  trigger.id ||= fallbackTriggerId;
  menuTriggerId.value = trigger.id;
  trigger.setAttribute("aria-haspopup", "menu");
  trigger.setAttribute("aria-expanded", "true");
  if (focusOnMenuOpen) {
    // The items become focusable menu items on the next render.
    nextTick(focusInitialMenuItem);
  }
}

function focusInitialMenuItem(): void {
  const popperNode = getPopperNode();
  if (!popperNode || !isShown.value || hideRequested || !isMenu.value) {
    return;
  }
  const item = initialMenuItem(popperNode);
  if (!item) {
    return;
  }
  focusOnMenuOpen = false;
  item.focus({ preventScroll: true });
}

// floating-vue focuses the popper itself two animation frames after it opens; in a menu focus belongs on an item.
// It does so even when the popper closed, or started closing, within those frames (an item picked or Escape pressed
// right away), which would leave focus in a hidden popper. That focus is undone: it goes back to the element it came from.
function onPopperFocusin(event: FocusEvent): void {
  const popperNode = getPopperNode();
  if (!popperNode || event.target !== popperNode) {
    return;
  }
  if (!isShown.value || hideRequested) {
    restoreFocusAfterLatePopperFocus(popperNode, event.relatedTarget);
    return;
  }
  if (isMenu.value) {
    focusInitialMenuItem();
  }
}

function restoreFocusAfterLatePopperFocus(popperNode: HTMLElement, previous: EventTarget | null): void {
  if (previous === null) {
    popperNode.blur();
    return;
  }
  if (previous instanceof HTMLElement && previous.isConnected && !popperNode.contains(previous)) {
    previous.focus({ preventScroll: true });
    return;
  }
  returnFocusFromPopper();
}

function onPopperKeydown(event: KeyboardEvent): void {
  const popperNode = getPopperNode();
  if (!isMenu.value || !popperNode) {
    return;
  }
  if (event.key === "Escape") {
    // Only the menu closes, not a dialog it sits in.
    event.preventDefault();
    event.stopPropagation();
    hide();
    return;
  }
  if (event.key === "Tab") {
    // Focus moves from the trigger, so Tab and Shift+Tab continue from where the menu was opened.
    getTriggerElement()?.focus({ preventScroll: true });
    hide();
    return;
  }
  const item = closestMenuItem(event.target);
  if ((event.key === "Enter" || event.key === " ") && item) {
    event.preventDefault();
    if (item.getAttribute("aria-disabled") !== "true") {
      item.click();
    }
    return;
  }
  if (focusMenuItemForKey(popperNode, event.key, item ?? document.activeElement)) {
    event.preventDefault();
  }
}

function onShow(): void {
  hideRequested = false;
  emit("show");
}

function onHide(): void {
  hideRequested = true;
  returnFocusFromPopper();
  emit("hide");
}

function onApplyShow(): void {
  isShown.value = true;
  focusOnMenuOpen = props.noAutoFocus !== true;
  observeContent();
  scheduleMenuCheck();
  emit("applyShow");
}

function onApplyHide(): void {
  isShown.value = false;
  focusOnMenuOpen = false;
  stopObservingContent();
  if (isMenu.value) {
    getTriggerElement()?.setAttribute("aria-expanded", "false");
  }
  emit("applyHide");
}

useLazyPopperActivation(rootEl, active, activate);

defineExpose({ show, hide, dispose, onResize, isShown });
</script>

<template>
  <div
    ref="rootEl"
    class="v-popper"
    :class="[themeClasses, { 'v-popper--shown': isShown }]"
    v-bind="attrs"
  >
    <slot :shown="isShown" :show="show" :hide="hide" />
    <LazyPopper
      v-if="active"
      ref="popper"
      v-bind="props"
      :reference-node="getReferenceNode"
      :target-nodes="getTargetNodes"
      :popper-node="getPopperNode"
      @show="onShow"
      @hide="onHide"
      @update:shown="(shown: boolean) => emit('update:shown', shown)"
      @apply-show="onApplyShow"
      @apply-hide="onApplyHide"
      @close-group="emit('closeGroup')"
      @close-directive="emit('closeDirective')"
      @auto-hide="emit('autoHide')"
      @resize="emit('resize')"
    >
      <template #default="slotData">
        <PopperContent
          ref="popperContent"
          :role="isMenu ? 'menu' : undefined"
          :aria-labelledby="isMenu ? menuTriggerId : undefined"
          :popper-id="slotData.popperId"
          :theme="theme"
          :shown="slotData.isShown"
          :mounted="slotData.shouldMountContent"
          :skip-transition="slotData.skipTransition"
          :auto-hide="slotData.autoHide"
          :handle-resize="slotData.handleResize"
          :classes="slotData.classes"
          :result="slotData.result"
          @hide="slotData.hide"
          @resize="slotData.onResize"
          @keydown="onPopperKeydown"
          @focusin="onPopperFocusin"
        >
          <slot name="popper" :shown="slotData.isShown" :hide="slotData.hide" />
        </PopperContent>
      </template>
    </LazyPopper>
  </div>
</template>
